import { enumKeyOf } from "_shared/_utilities/enum-utils";
import { AccessTypeEnumByResourceType } from "permissions/_views/permission-table-view-model";
import { DocumentContentsAccess } from "permissions/resources/documents/documents-permissions-resources";
import { DocumentSubjectMatter } from "permissions/resources/documents/document-subject-matter";
import type { Resource } from "permissions/resources/permission-resource";
import { AccessLevel, ResourceType, Scope } from "permissions/resources/permission-resource-types";

/** Labels that differ from the type's own name; every other type is named from its PascalCase. */
const RESOURCE_TYPE_LABELS: Partial<Record<ResourceType, string>> = {
  [ResourceType.DocumentContentsAccess]: "Document Subject Matter",
  [ResourceType.LegalEntityAccess]: "Legal Entities",
  [ResourceType.GeneralDocumentAccess]: "General Documents",
  [ResourceType.DocumentAccess]: "Documents",
  [ResourceType.NotificationsAccess]: "Notification Center Access",
};

/** A resource type as a reader names it: `ProposalsAccess` is "Proposals". */
export function labelFromResourceType(resourceType: ResourceType): string {
  return (
    RESOURCE_TYPE_LABELS[resourceType] ??
    resourceType
      .replaceAll(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replaceAll(/Access$/g, "")
      .trim()
  );
}

/**
 * The part of a resource's identity that its type name leaves out — the access type of a
 * legal-entity grant, the subject matter of a document-contents grant — so two rows of the
 * same type are told apart.
 */
function resourceQualifier(resource: Resource): string | undefined {
  if (resource instanceof DocumentContentsAccess) {
    return enumKeyOf(DocumentSubjectMatter, resource.subjectMatter) || undefined;
  }
  if ("accessType" in resource) {
    const accessTypeEnum =
      AccessTypeEnumByResourceType[resource.resourceType as keyof typeof AccessTypeEnumByResourceType];
    if (accessTypeEnum) return enumKeyOf(accessTypeEnum, resource.accessType) || undefined;
  }
  return undefined;
}

/** Names the thing a permission covers, e.g. `Documents` or `Legal Entities — Invoices`. */
export function describeResource(resource: Resource): string {
  const label = labelFromResourceType(resource.resourceType);
  const qualifier = resourceQualifier(resource);
  return qualifier ? `${label} — ${qualifier}` : label;
}

/** Names how much of it the permission gives, e.g. `Read/Write · Entity-Wide`. */
export function describeAccess(resource: Resource): string {
  const parts: string[] = [];
  if ("accessLevel" in resource) parts.push(enumKeyOf(AccessLevel, resource.accessLevel));
  if ("scope" in resource) parts.push(enumKeyOf(Scope, resource.scope));
  return parts.filter((part) => part.length > 0).join(" · ");
}

/**
 * Orders resources the way they are read rather than the way a template happened to store
 * them, so the same template lists its permissions identically wherever it is shown.
 */
export function sortResourcesForDisplay(resources: readonly Resource[]): Resource[] {
  return [...resources].sort((left, right) =>
    describeResource(left).localeCompare(describeResource(right), undefined, { sensitivity: "base" }),
  );
}
